import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { bindActionCreators } from "redux";
import { Form as BootstrapForm } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";

import { DropDownList } from "../../common/dropDownList/dropDownList";
import CountryDropdown from "../../common/CountryDropdown";
import { getSelectedCompany, isEmpty } from "../../Global/CustomFunctions";
import { Common } from "../../../utils";
import { WebStorage, WebStorageNames } from "../../../utils";
import { needsAdminOnboarding } from "../../../utils/onboardingUtils";
import { ATTENDANCE_AGENT_DOWNLOAD_URL } from "../../../utils/attendanceAgentUtils";
import {
  captureOnboardingEvent,
  captureOnboardingException,
} from "../../../utils/onboardingAnalytics";
import { fetchSubscriptionData, isSubscriptionSuspended } from "../../../utils/subscriptionUtils";
import { updateUserAnnualLeaveFlag } from "../accounts/accountActions";
import * as officeActions from "../company/companyOffices/officeActions";
import LanguageSelector from "../../common/header/LanguageSelector";

import "./OnboardingPage.css";

const TOTAL_STEPS = 3;
const LOGO_SRC = "/assets/img/logo2.png";

const AGENT_FLOW = [
  { icon: "bi-fingerprint", titleKey: "agentFlowDevice", hintKey: "agentFlowDeviceHint" },
  { icon: "bi-pc-display", titleKey: "agentFlowAgent", hintKey: "agentFlowAgentHint" },
  { icon: "bi-cloud-check", titleKey: "agentFlowCloud", hintKey: "agentFlowCloudHint" },
];

const AGENT_PREP_STEPS = [
  { icon: "bi-download", textKey: "setupStep1" },
  { icon: "bi-box-arrow-in-right", textKey: "setupStep2" },
];

// Reuses the walkthrough shown on Attendance → Machines & Agents.
const AGENT_GUIDE_STEPS = [
  {
    titleKey: "guideStep1Title",
    bodyKey: "guideStep1Body",
    altKey: "guideStep1ImgAlt",
    image: "/assets/attendance-agent/guide/guide-same-network.png?v=2",
  },
  {
    titleKey: "guideStep2Title",
    bodyKey: "guideStep2Body",
    altKey: "guideStep2ImgAlt",
    image: "/assets/attendance-agent/guide/guide-set-machine-ip.png?v=2",
  },
  {
    titleKey: "guideStep3Title",
    bodyKey: "guideStep3Body",
    altKey: "guideStep3ImgAlt",
    image: "/assets/attendance-agent/guide/guide-enter-ip-in-agent.png?v=2",
  },
];

const STEP_COPY = {
  1: { heading: "screenHeadingStep1", lead: "screenLeadStep1" },
  2: { heading: "screenHeadingStep2", lead: "screenLeadStep2" },
  3: { heading: "screenHeadingStep3", lead: "screenLeadStep3" },
};

const OnboardingPage = ({
  t,
  history,
  dispatch,
  officeReducer,
  officeActions: actions,
  updateAnnualLeaveFlag,
  isAnnualLeavePopupShown,
}) => {
  const [step, setStep] = useState(1);
  const [balance, setBalance] = useState("");
  const [leaveError, setLeaveError] = useState("");
  const [workplaceSubmitting, setWorkplaceSubmitting] = useState(false);
  const [workplaceCreated, setWorkplaceCreated] = useState(false);
  const [subscriptionSuspended, setSubscriptionSuspended] = useState(false);

  const finishOnboarding = useCallback(
    (completionMethod) => {
      captureOnboardingEvent("onboarding_completed", {
        completionMethod,
        finalStep: step,
      });
      dispatch({ type: "SET_ANNUAL_LEAVE_POPUP_SHOWN", payload: true });
      WebStorage.setLocalStore(WebStorageNames.IsAnnualLeavePopupShown, true);
      history.push("/workplaces");
    },
    [dispatch, history, step]
  );

  useEffect(() => {
    if (!needsAdminOnboarding(isAnnualLeavePopupShown)) {
      captureOnboardingEvent("onboarding_redirect_already_complete");
      history.replace("/workplaces");
      return;
    }

    const company = getSelectedCompany();
    if (isEmpty(company)) {
      captureOnboardingEvent("onboarding_redirect_no_company");
      history.replace("/");
      return;
    }

    captureOnboardingEvent("onboarding_started", { step: 1 });

    actions.onGetCountries();
    actions.onGetBusinessDaysPresets();

    fetchSubscriptionData()
      .then((data) => {
        const suspended = !data?.subscription || isSubscriptionSuspended(data);
        if (suspended) {
          setSubscriptionSuspended(true);
          captureOnboardingEvent("onboarding_subscription_suspended");
        }
      })
      .catch((error) => {
        setSubscriptionSuspended(true);
        captureOnboardingException(
          "onboarding_subscription_check",
          error?.message ?? "Subscription check failed"
        );
      });
  }, [actions, history, isAnnualLeavePopupShown]);

  useEffect(() => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      captureOnboardingEvent("onboarding_step_viewed", { step });
    }
  }, [step]);

  const businessDaysOptions = useMemo(() => {
    const presets = officeReducer.businessDaysPresets || [];
    return presets.map((preset) => ({
      key: preset.value,
      text: preset.label || preset.text,
      value: Number(preset.value),
      description: preset.description,
    }));
  }, [officeReducer.businessDaysPresets]);

  const workplaceValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required(t("app.officeDashboard.validationNameRequired"))
          .max(200, t("app.officeDashboard.validationNameMax")),
        countryId: Yup.number().required(t("app.officeDashboard.validationLocationRequired")),
        stateId: Yup.number()
          .nullable()
          .transform((value, originalValue) => (originalValue === "" ? null : value)),
        address: Yup.string()
          .optional()
          .max(1000, t("app.officeDashboard.validationAddressMax")),
        businessDaysValue: Yup.number().required(
          t("app.officeDashboard.validationBusinessDaysRequired")
        ),
      }),
    [t]
  );

  const validateLeaveBalance = () => {
    const trimmed = (balance || "").toString().trim();
    if (trimmed !== "") {
      const num = parseFloat(trimmed);
      if (isNaN(num) || num < 0) {
        setLeaveError(t("app.onboarding.leaveBalanceInvalid"));
        return undefined;
      }
      if (num > 365) {
        setLeaveError(t("app.onboarding.leaveBalanceMax"));
        return undefined;
      }
      return num;
    }
    return null;
  };

  const goToStep = (target) => {
    setStep(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveLeaveAndGoToStep2 = (leaveValue, source) => {
    updateAnnualLeaveFlag(leaveValue, source);
    goToStep(2);
  };

  const handleStep1Next = () => {
    const leaveValue = validateLeaveBalance();
    if (leaveValue === undefined) return;
    setLeaveError("");
    captureOnboardingEvent("onboarding_step1_continue", {
      leaveBalanceProvided: leaveValue != null,
      leaveBalance: leaveValue,
    });
    saveLeaveAndGoToStep2(leaveValue, "continue");
  };

  const handleSkipStep = () => {
    if (step === 1) {
      setLeaveError("");
      captureOnboardingEvent("onboarding_step_skipped", { step: 1, action: "advance_to_step2" });
      saveLeaveAndGoToStep2(null, "skip_step");
    } else if (step === 2) {
      captureOnboardingEvent("onboarding_step_skipped", { step: 2, action: "advance_to_step3" });
      goToStep(3);
    } else {
      captureOnboardingEvent("onboarding_step_skipped", { step: 3, action: "complete_without_agent" });
      finishOnboarding("skip_step3");
    }
  };

  const handleSkipEntireSetup = () => {
    captureOnboardingEvent("onboarding_skipped_entire", { step });
    finishOnboarding("skip_entire");
  };

  const handleStepTabClick = (targetStep) => {
    if (targetStep === step) return;

    if (targetStep < step) {
      // Once the workplace exists, going back would let the admin create it twice.
      if (workplaceCreated) return;
      captureOnboardingEvent("onboarding_step_tab_clicked", {
        fromStep: step,
        toStep: targetStep,
        direction: "back",
      });
      goToStep(targetStep);
      return;
    }

    // Forward: leaving step 1 saves the leave balance; later steps can be skipped over.
    const eventProps = { fromStep: step, toStep: targetStep, direction: "forward" };
    if (step === 1) {
      const leaveValue = validateLeaveBalance();
      if (leaveValue === undefined) return;
      setLeaveError("");
      updateAnnualLeaveFlag(leaveValue, "tab_click");
      eventProps.leaveBalanceProvided = leaveValue != null;
    }
    captureOnboardingEvent("onboarding_step_tab_clicked", eventProps);
    goToStep(targetStep);
  };

  const handleBack = () => {
    captureOnboardingEvent("onboarding_back_clicked", { fromStep: step, toStep: step - 1 });
    goToStep(step - 1);
  };

  const handleAgentDownloadClick = () => {
    captureOnboardingEvent("onboarding_agent_download_clicked", { step: 3 });
  };

  const handleCreateWorkplace = async (values) => {
    if (subscriptionSuspended) {
      captureOnboardingEvent("onboarding_workplace_blocked", {
        reason: "subscription_suspended",
      });
      Common.showErrorMessage(t("app.officeDashboard.errorSubscriptionSuspendedAdmin"));
      return { success: false };
    }

    captureOnboardingEvent("onboarding_workplace_submit", {
      countryId: values.countryId,
      hasState: values.stateId != null && values.stateId !== "",
      hasAddress: !!(values.address && String(values.address).trim()),
    });

    const cleanedValues = { ...values };
    if (
      cleanedValues.stateId === "" ||
      cleanedValues.stateId === null ||
      cleanedValues.stateId === undefined
    ) {
      delete cleanedValues.stateId;
    }

    await actions.isLoading(true);
    const result = await actions.onAddOffice(cleanedValues, history, { redirect: false });

    if (result?.success) {
      actions.isLoading(false);
      captureOnboardingEvent("onboarding_workplace_created", { success: true });
    } else {
      captureOnboardingException(
        "onboarding_workplace_created",
        result?.message ?? "Workplace creation failed",
        { success: false }
      );
    }

    return result;
  };

  const stepTabs = [
    {
      number: 1,
      label: t("app.onboarding.screenStep1Label"),
      title: t("app.onboarding.screenStep1Title"),
    },
    {
      number: 2,
      label: t("app.onboarding.screenStep2Label"),
      title: t("app.onboarding.screenStep2Title"),
    },
    {
      number: 3,
      label: t("app.onboarding.screenStep3Label"),
      title: t("app.onboarding.screenStep3Title"),
    },
  ];

  const heading = t(`app.onboarding.${STEP_COPY[step].heading}`);
  const lead = t(`app.onboarding.${STEP_COPY[step].lead}`);

  return (
    <div className="onboarding-page">
      <header className="onboarding-top">
        <div className="onboarding-top-inner">
          <img src={LOGO_SRC} alt={t("app.auth.logoAlt")} className="onboarding-logo" />

          <nav className="onboarding-steps" aria-label={t("app.onboarding.progressAria")}>
          {stepTabs.map((tab) => {
            const isActive = step === tab.number;
            const isDone = step > tab.number;
            const isAhead = tab.number > step;
            // Going back is blocked once the workplace exists, otherwise it could be created twice.
            const isClickable = !isActive && !(isDone && workplaceCreated);

            return (
              <button
                key={tab.number}
                type="button"
                className={`onboarding-step-tab${isActive ? " onboarding-step-tab--active" : ""}${isDone ? " onboarding-step-tab--done" : ""}${isClickable ? " onboarding-step-tab--clickable" : ""}${isAhead && isClickable ? " onboarding-step-tab--ahead" : ""}`}
                onClick={() => isClickable && handleStepTabClick(tab.number)}
                aria-current={isActive ? "step" : undefined}
                aria-disabled={!isClickable}
              >
                <span className="onboarding-step-tab-label">
                  {tab.number}) {tab.label}
                </span>
                <span className="onboarding-step-tab-title">{tab.title}</span>
              </button>
            );
          })}
          </nav>

          <div className="onboarding-top-actions">
            <div className="onboarding-language-wrap">
              <LanguageSelector skipUserDetailsSync />
            </div>
            <button type="button" className="onboarding-skip" onClick={handleSkipStep}>
              <span className="onboarding-skip-label">{t("app.onboarding.skipForNow")}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="onboarding-main">
        <h1 className="onboarding-heading">{heading}</h1>
        <p className="onboarding-lead">{lead}</p>

        {step === 1 ? (
          <div className="onboarding-form-wrap">
            <div className="onboarding-callout">{t("app.onboarding.step1Info")}</div>

            <div className="onboarding-field">
              <label className="onboarding-field-label" htmlFor="onboarding-leave-balance">
                {t("app.onboarding.leaveBalanceLabel")}{" "}
                <span className="onboarding-field-label-optional">
                  {t("app.onboarding.optional")}
                </span>
              </label>
              <input
                id="onboarding-leave-balance"
                type="number"
                min={0}
                max={365}
                className={`onboarding-input${leaveError ? " onboarding-input--error" : ""}`}
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder={t("app.onboarding.leaveBalancePlaceholder")}
              />
              {leaveError && <p className="onboarding-field-error">{leaveError}</p>}
              <p className="onboarding-field-hint">{t("app.onboarding.step1Hint")}</p>
            </div>

            <div className="onboarding-skip-notice" role="note">
              <p className="onboarding-skip-notice-text">{t("app.onboarding.skipAllHint")}</p>
              <button
                type="button"
                className="onboarding-skip-notice-link"
                onClick={handleSkipEntireSetup}
              >
                {t("app.onboarding.skipEntireSetup")}
              </button>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="onboarding-content-layout onboarding-content-layout--step2">
            <div className="onboarding-form-wrap">
              {subscriptionSuspended && (
                <div className="onboarding-warning" role="alert">
                  {t("app.officeDashboard.errorSubscriptionSuspendedAdmin")}
                </div>
              )}

              <Formik
                enableReinitialize
                initialValues={{
                  name: "",
                  countryId: "",
                  stateId: "",
                  address: "",
                  businessDaysValue: 62,
                }}
                validationSchema={workplaceValidationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  if (subscriptionSuspended) {
                    setSubmitting(false);
                    return;
                  }
                  setWorkplaceSubmitting(true);
                  try {
                    const result = await handleCreateWorkplace(values);
                    if (result?.success) {
                      setWorkplaceCreated(true);
                      goToStep(3);
                    }
                  } finally {
                    setWorkplaceSubmitting(false);
                    setSubmitting(false);
                  }
                }}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                  setFieldValue,
                }) => (
                  <BootstrapForm id="onboarding-workplace-form" onSubmit={handleSubmit}>
                    <div className="onboarding-field">
                      <label className="onboarding-field-label" htmlFor="onboarding-wp-name">
                        {t("app.officeDashboard.workplaceName")}
                      </label>
                      <input
                        id="onboarding-wp-name"
                        type="text"
                        name="name"
                        className={`onboarding-input${touched.name && errors.name ? " onboarding-input--error" : ""}`}
                        placeholder={t("app.officeDashboard.workplaceNamePlaceholder")}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.name && errors.name && (
                        <p className="onboarding-field-error">{errors.name}</p>
                      )}
                    </div>

                    <div className="onboarding-field">
                      <label className="onboarding-field-label">
                        {t("app.officeDashboard.country")}
                      </label>
                      <CountryDropdown
                        selection
                        search
                        fluid
                        value={values.countryId}
                        options={officeReducer.countries || []}
                        placeholder={t("app.officeDashboard.selectCountry")}
                      className="w-100 onboarding-dropdown"
                      name="countryId"
                        onChange={(e, { value }) => {
                          setFieldValue("countryId", value);
                          setFieldValue("stateId", "");
                          if (value) actions.onGetStatesByCountryId(value);
                        }}
                        loading={officeReducer.isLoading}
                      />
                      {touched.countryId && errors.countryId && (
                        <p className="onboarding-field-error">{errors.countryId}</p>
                      )}
                    </div>

                    <div className="onboarding-field">
                      <label className="onboarding-field-label">
                        {t("app.officeDashboard.stateProvince")}
                      </label>
                      <DropDownList
                        selection
                        fluid
                        value={values.stateId}
                        options={officeReducer.states || []}
                        placeholder={t("app.officeDashboard.selectStateProvince")}
                      className="w-100 onboarding-dropdown"
                      name="stateId"
                        disabled={!values.countryId}
                        onChange={(e, { value }) => setFieldValue("stateId", value)}
                        loading={officeReducer.isLoading}
                      />
                    </div>

                    <div className="onboarding-field">
                      <label className="onboarding-field-label">
                        {t("app.officeDashboard.businessDays")}
                      </label>
                      <DropDownList
                        selection
                        fluid
                        value={values.businessDaysValue}
                        options={businessDaysOptions}
                        placeholder={t("app.officeDashboard.selectBusinessDays")}
                      className="w-100 onboarding-dropdown"
                      name="businessDaysValue"
                        onChange={(e, { value }) => setFieldValue("businessDaysValue", value)}
                        loading={officeReducer.isLoading}
                      />
                      {touched.businessDaysValue && errors.businessDaysValue && (
                        <p className="onboarding-field-error">{errors.businessDaysValue}</p>
                      )}
                    </div>

                    <div className="onboarding-field">
                      <label className="onboarding-field-label" htmlFor="onboarding-wp-address">
                        {t("app.officeDashboard.streetAddress")}{" "}
                        <span className="onboarding-field-label-optional">
                          {t("app.onboarding.optional")}
                        </span>
                      </label>
                      <textarea
                        id="onboarding-wp-address"
                        name="address"
                        rows={2}
                        className={`onboarding-input${touched.address && errors.address ? " onboarding-input--error" : ""}`}
                        placeholder={t("app.officeDashboard.streetAddressPlaceholder")}
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.address && errors.address && (
                        <p className="onboarding-field-error">{errors.address}</p>
                      )}
                    </div>
                  </BootstrapForm>
                )}
              </Formik>
            </div>
            <aside className="onboarding-tips-column">
              <div className="onboarding-callout onboarding-tip-box">
                {t("app.onboarding.step2Info")}
              </div>
              <div className="onboarding-skip-notice onboarding-tip-box" role="note">
                <p className="onboarding-skip-notice-text">{t("app.onboarding.skipAllHint")}</p>
                <button
                  type="button"
                  className="onboarding-skip-notice-link"
                  onClick={handleSkipEntireSetup}
                >
                  {t("app.onboarding.skipEntireSetup")}
                </button>
              </div>
            </aside>
          </div>
        ) : (
          <div className="onboarding-agent">
            <div className="onboarding-agent-card">
              <div className="onboarding-agent-card-icon" aria-hidden>
                <i className="bi bi-pc-display" />
              </div>
              <div className="onboarding-agent-card-text">
                <h2 className="onboarding-agent-card-title">{t("app.onboarding.agentCardTitle")}</h2>
                <p className="onboarding-agent-card-meta">{t("app.onboarding.agentCardMeta")}</p>
              </div>
              <a
                className="onboarding-btn onboarding-btn--orange onboarding-agent-download"
                href={ATTENDANCE_AGENT_DOWNLOAD_URL}
                rel="noopener noreferrer"
                download
                onClick={handleAgentDownloadClick}
              >
                <i className="bi bi-download" aria-hidden />
                {t("app.attendance.machines.downloadAgent")}
              </a>
            </div>

            <ol className="onboarding-agent-flow">
              {AGENT_FLOW.map((node, index) => (
                <React.Fragment key={node.titleKey}>
                  {index > 0 && (
                    <li className="onboarding-agent-flow-arrow" aria-hidden>
                      <i className="bi bi-arrow-right" />
                    </li>
                  )}
                  <li className="onboarding-agent-flow-node">
                    <span className="onboarding-agent-flow-icon" aria-hidden>
                      <i className={`bi ${node.icon}`} />
                    </span>
                    <span className="onboarding-agent-flow-title">
                      {t(`app.onboarding.${node.titleKey}`)}
                    </span>
                    <span className="onboarding-agent-flow-hint">
                      {t(`app.onboarding.${node.hintKey}`)}
                    </span>
                  </li>
                </React.Fragment>
              ))}
            </ol>

            <div className="onboarding-callout onboarding-agent-note">
              {t("app.onboarding.step3Info")}
            </div>

            <section className="onboarding-agent-guide" aria-labelledby="onboarding-agent-guide-title">
              <h2 id="onboarding-agent-guide-title" className="onboarding-agent-guide-title">
                {t("app.onboarding.agentGuideTitle")}
              </h2>
              <p className="onboarding-agent-guide-lead">{t("app.onboarding.agentGuideLead")}</p>

              <h3 className="onboarding-agent-guide-subtitle">
                {t("app.onboarding.agentGuidePrepTitle")}
              </h3>
              <ul className="onboarding-agent-prep">
                {AGENT_PREP_STEPS.map((item) => (
                  <li key={item.textKey} className="onboarding-agent-prep-item">
                    <i className={`bi ${item.icon}`} aria-hidden />
                    <span>{t(`app.attendance.machines.${item.textKey}`)}</span>
                  </li>
                ))}
              </ul>

              <h3 className="onboarding-agent-guide-subtitle">
                {t("app.attendance.machines.guideTitle")}
              </h3>
              <p className="onboarding-agent-guide-note">{t("app.attendance.machines.guideLead")}</p>
              <div className="onboarding-agent-steps">
                {AGENT_GUIDE_STEPS.map((guide) => (
                  <article key={guide.titleKey} className="onboarding-agent-step">
                    <a
                      className="onboarding-agent-step-img"
                      href={guide.image}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={guide.image}
                        alt={t(`app.attendance.machines.${guide.altKey}`)}
                        loading="lazy"
                      />
                    </a>
                    <div className="onboarding-agent-step-body">
                      <h4 className="onboarding-agent-step-title">
                        {t(`app.attendance.machines.${guide.titleKey}`)}
                      </h4>
                      <p className="onboarding-agent-step-text">
                        {t(`app.attendance.machines.${guide.bodyKey}`)}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        )}
      </main>

      <footer className="onboarding-footer">
        <div className="onboarding-footer-inner">
          {step > 1 && !workplaceCreated ? (
            <button
              type="button"
              className="onboarding-btn onboarding-btn--ghost"
              onClick={handleBack}
            >
              {t("app.onboarding.back")}
            </button>
          ) : (
            <span />
          )}

          {step === 1 ? (
            <button
              type="button"
              className="onboarding-btn onboarding-btn--primary"
              onClick={handleStep1Next}
            >
              {t("app.onboarding.next")}
            </button>
          ) : step === 3 ? (
            <button
              type="button"
              className="onboarding-btn onboarding-btn--primary"
              onClick={() => finishOnboarding("agent_step_finished")}
            >
              {t("app.onboarding.finishSetup")}
            </button>
          ) : (
            <button
              type="submit"
              form="onboarding-workplace-form"
              className="onboarding-btn onboarding-btn--primary"
              disabled={
                subscriptionSuspended || officeReducer.isLoading || workplaceSubmitting
              }
            >
              {workplaceSubmitting
                ? t("app.officeDashboard.creatingWorkplace")
                : t("app.onboarding.createWorkplace")}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

const mapStateToProps = ({ officeReducer, accountReducer }) => ({
  officeReducer,
  isAnnualLeavePopupShown: accountReducer?.isAnnualLeavePopupShown,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  officeActions: bindActionCreators(officeActions, dispatch),
  updateAnnualLeaveFlag: (balance, source = "unknown") => {
    updateUserAnnualLeaveFlag(balance)
      .then(() => {
        captureOnboardingEvent("onboarding_leave_balance_saved", {
          source,
          leaveBalanceProvided: balance != null,
          leaveBalance: balance,
        });
      })
      .catch((error) => {
        captureOnboardingException(
          "onboarding_leave_balance_saved",
          error?.response?.data?.message ?? error?.message ?? "Request failed",
          { source, leaveBalanceProvided: balance != null }
        );
      });
  },
});

export default withRouter(
  withTranslation()(connect(mapStateToProps, mapDispatchToProps)(OnboardingPage))
);
